/**
 * CRM roster — one row per patient, customer-first. Rich filter bar:
 * derived status / partner / product / state / warnings / join-date, plus
 * free-text search. Clicking a row drills into the per-customer detail.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useClinicalPatients } from '@/lib/api/clinicalAdminHooks';
import type { PatientCard } from '@/types/clinicalAdmin';

import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Td,
  Th,
  fmtDate,
  isTestEmail,
  onboardingLabel,
} from './ui';

const PAGE_SIZE = 50;

// Derived order statuses — `awaiting_prescription` is split by the backend
// into the clinician worklist's stage (who owns the next step).
const STATUS_OPTIONS = [
  'awaiting_intake',
  'intake_processing',
  'awaiting_consult',
  'awaiting_id',
  'missing_info',
  'awaiting_rx',
  'submission_pending',
  'on_hold',
  'awaiting_pharmacy_routing',
  'pending_review',
  'processing',
  'shipped',
  'delivered',
  'cancelled',
  'failed',
];

const PARTNER_OPTIONS: { value: string; label: string }[] = [
  { value: 'precisionmeds', label: 'PrecisionMeds' },
  { value: 'the_pharmacy_hub', label: 'The Pharmacy Hub' },
  { value: 'optiorx', label: 'OptioRx' },
];

function prettyState(value: string | null): string {
  if (!value) return '—';
  return value.replace(/_/g, ' ');
}

const WAITING_STATUSES = new Set([
  'pending_review',
  'intake_processing',
  'missing_info',
  'submission_pending',
  'on_hold',
]);

function statusTone(
  status: string | null,
): 'good' | 'warn' | 'danger' | 'neutral' {
  if (!status) return 'neutral';
  if (status === 'delivered' || status === 'shipped') return 'good';
  if (status === 'failed' || status === 'cancelled') return 'danger';
  if (status.startsWith('awaiting') || WAITING_STATUSES.has(status))
    return 'warn';
  return 'neutral';
}

const SELECT_CLS =
  'type-label-sm px-10 py-8 rounded border border-line bg-raised';

export function CustomerTable() {
  const navigate = useNavigate();
  // Debounced text inputs.
  const [searchInput, setSearchInput] = useState('');
  const [productInput, setProductInput] = useState('');
  const [stateInput, setStateInput] = useState('');
  const [search, setSearch] = useState('');
  const [productId, setProductId] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  // Immediate filters.
  const [status, setStatus] = useState('');
  const [partnerKey, setPartnerKey] = useState('');
  const [hasWarnings, setHasWarnings] = useState(false);
  const [joinedAfter, setJoinedAfter] = useState('');
  const [joinedBefore, setJoinedBefore] = useState('');
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setProductId(productInput.trim());
      setStateFilter(stateInput.trim());
      setOffset(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, productInput, stateInput]);

  const { data, isLoading, isError } = useClinicalPatients({
    search,
    status,
    partnerKey,
    productId,
    state: stateFilter,
    hasWarnings,
    joinedAfter,
    joinedBefore,
    offset,
    limit: PAGE_SIZE,
  });

  const patients = data?.patients ?? [];
  const total = data?.total ?? 0;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  const resetOffset = () => setOffset(0);
  const clearAll = () => {
    setSearchInput('');
    setProductInput('');
    setStateInput('');
    setStatus('');
    setPartnerKey('');
    setHasWarnings(false);
    setJoinedAfter('');
    setJoinedBefore('');
    setOffset(0);
  };
  const anyFilter =
    search ||
    productId ||
    stateFilter ||
    status ||
    partnerKey ||
    hasWarnings ||
    joinedAfter ||
    joinedBefore;

  return (
    <div className="flex flex-col gap-16">
      <div className="flex flex-col gap-10">
        <div className="flex flex-wrap items-center gap-10">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, email, Healthie ID, order #…"
            className="type-label-sm px-12 py-8 rounded border border-line bg-raised min-w-[240px]"
          />
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              resetOffset();
            }}
            className={SELECT_CLS}
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {prettyState(s)}
              </option>
            ))}
          </select>
          <select
            value={partnerKey}
            onChange={(e) => {
              setPartnerKey(e.target.value);
              resetOffset();
            }}
            className={SELECT_CLS}
          >
            <option value="">All pharmacies</option>
            {PARTNER_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={productInput}
            onChange={(e) => setProductInput(e.target.value)}
            placeholder="Product ID contains…"
            className="type-label-sm px-12 py-8 rounded border border-line bg-raised w-[180px]"
          />
          <input
            type="text"
            value={stateInput}
            onChange={(e) => setStateInput(e.target.value)}
            placeholder="State"
            maxLength={2}
            className="type-label-sm px-12 py-8 rounded border border-line bg-raised w-[80px] uppercase"
          />
          <label className="flex items-center gap-6 type-label-sm text-ink-muted">
            <input
              type="checkbox"
              checked={hasWarnings}
              onChange={(e) => {
                setHasWarnings(e.target.checked);
                resetOffset();
              }}
            />
            Has warnings
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-10">
          <label className="flex items-center gap-6 type-label-sm text-ink-muted">
            Joined after
            <input
              type="date"
              value={joinedAfter}
              onChange={(e) => {
                setJoinedAfter(e.target.value);
                resetOffset();
              }}
              className={SELECT_CLS}
            />
          </label>
          <label className="flex items-center gap-6 type-label-sm text-ink-muted">
            before
            <input
              type="date"
              value={joinedBefore}
              onChange={(e) => {
                setJoinedBefore(e.target.value);
                resetOffset();
              }}
              className={SELECT_CLS}
            />
          </label>
          {anyFilter && (
            <button
              type="button"
              onClick={clearAll}
              className="type-label-sm px-10 py-6 rounded border border-line hover:bg-raised"
            >
              Clear filters
            </button>
          )}
          {!isLoading && !isError && (
            <span className="type-label-sm text-ink-muted ml-auto">
              {total === 0
                ? 'No customers'
                : `${offset + 1}–${pageEnd} of ${total}`}
            </span>
          )}
        </div>
      </div>

      <Card>
        {isLoading && <LoadingState label="Loading customers…" />}
        {isError && <ErrorState>Could not load the customer roster.</ErrorState>}
        {!isLoading && !isError && patients.length === 0 && (
          <EmptyState>No customers match these filters.</EmptyState>
        )}
        {!isLoading && !isError && patients.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken">
                <tr>
                  <Th>Customer</Th>
                  <Th>Status</Th>
                  <Th>Onboarding</Th>
                  <Th>Orders</Th>
                  <Th>Pharmacy</Th>
                  <Th>Warnings</Th>
                  <Th>Last order</Th>
                  <Th>Joined</Th>
                </tr>
              </thead>
              <tbody>
                {patients.map((p) => (
                  <CustomerRow
                    key={p.id}
                    patient={p}
                    onOpen={() =>
                      navigate(`/admin/clinical-admin/customers/${p.id}`)
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-10">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="type-label-sm px-12 py-6 rounded border border-line hover:bg-raised disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={pageEnd >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="type-label-sm px-12 py-6 rounded border border-line hover:bg-raised disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function CustomerRow({
  patient,
  onOpen,
}: {
  patient: PatientCard;
  onOpen: () => void;
}) {
  const warnings = patient.open_warnings ?? 0;
  const derived = patient.derived_status;
  return (
    <tr
      onClick={onOpen}
      className="border-t border-line-subtle hover:bg-sunken cursor-pointer"
    >
      <Td>
        <div className="flex flex-col">
          <span className="text-ink font-medium flex items-center gap-6">
            {patient.display_name || '(no name)'}
            {isTestEmail(patient.email) && <Badge tone="warn">TEST</Badge>}
          </span>
          <span className="text-ink-muted">{patient.email || '—'}</span>
        </div>
      </Td>
      <Td>
        {derived ? (
          <Badge tone={statusTone(derived)}>{prettyState(derived)}</Badge>
        ) : (
          <span className="text-ink-muted">no orders</span>
        )}
      </Td>
      <Td>{onboardingLabel(patient.onboarding_state)}</Td>
      <Td>{patient.order_count ?? 0}</Td>
      <Td>
        {patient.partners && patient.partners.length > 0
          ? patient.partners.join(', ')
          : '—'}
      </Td>
      <Td>
        {warnings > 0 ? (
          <Badge tone="danger">{warnings} open</Badge>
        ) : (
          <span className="text-ink-muted">—</span>
        )}
      </Td>
      <Td>{fmtDate(patient.last_order_at)}</Td>
      <Td>{fmtDate(patient.created_at)}</Td>
    </tr>
  );
}
